import React, { useState, useMemo } from "react";
import { CashModalProps } from "../../utils/types";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import { User } from "lucide-react";
import { splitDateTime } from "src/shared/components/dateTime";

const CashModal: React.FC<CashModalProps> = ({ order, paymentMethod, receivedPrice, setReceivedPrice, handleCloseModal, handleConfirmPayment }) => {
  // ✅ Always declare hooks at the top
  const [errorText, setErrorText] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get car numbers from products' services using useMemo
  const { carNumbers, totalCars, carNumbersText } = useMemo(() => {
    const cars: string[] = [];

    if (order?.orderDetails?.[0]?.products) {
      const products = order.orderDetails[0].products;

      products.forEach((product: any) => {
        const services = product?.service || [];
        services.forEach((service: any) => {
          if (service?.carNumber) {
            cars.push(service.carNumber);
          }
        });
      });
    }

    const uniqueCars = Array.from(new Set(cars)); // Remove duplicates
    return {
      carNumbers: uniqueCars,
      totalCars: uniqueCars.length,
      carNumbersText: uniqueCars.join(", ") || "N/A",
    };
  }, [order]);

  // Calculate derived values
  const remainingAmount = useMemo(() => order?.orders?.[0]?.remainingAmount || order?.totalOutstandingBalance || 0, [order]);

  const customerName = useMemo(() => (order?.customerDetails ? `${order.customerDetails.firstName} ${order.customerDetails.lastName || ""}`.trim() : "N/A"), [order]);

  const totalReceivedAmount = useMemo(() => order?.paymentHistory?.reduce((total: number, payment: any) => total + (payment.receivedAmount || 0), 0) || 0, [order]);

  const totalOrderAmount = useMemo(() => order?.orders?.[0]?.finalAmount || order?.orders?.[0]?.orderAmount || 0, [order]);

  const { date: orderDate, time: orderTime } = useMemo(() => splitDateTime(order?.orders?.[0]?.orderedOn?.$date ?? ""), [order]);

  const handleReceivedPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value) || 0;
    const newValue = Math.min(Math.max(0, val), remainingAmount);
    setReceivedPrice(newValue);

    if (newValue !== remainingAmount) {
      setErrorText("Full payment only");
    } else {
      setErrorText("");
    }
  };

  const handleMouseEnter = () => {
    if (totalCars > 0) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleCompletePayment = async () => {
    if (Number(receivedPrice) !== remainingAmount || receivedPrice <= 0) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await handleConfirmPayment(paymentMethod);
      // Modal will be closed by the parent component after successful payment
    } catch (error) {
      console.error("Payment failed:", error);
      setIsProcessing(false);
    }
  };

  // ✅ Safe conditional return after all hooks
  if (!order) return null;

  return (
    <ModalWrapper title="Payment Summary" onClose={handleCloseModal} width="max-w-lg">
      <div className="space-y-5 text-sm text-gray-700">
        {/* Header Section */}
        <div className="bg-gray-50 border rounded-lg p-4 flex items-center gap-3">
          <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
            <User className="text-blue-600" size={24} />
          </div>

          <div className="flex flex-col">
            <div className="font-semibold text-gray-900">
              Customer: <span className="text-gray-800">{customerName}</span>
            </div>
            <div className="text-sm text-gray-600">
              Payment: <span className="capitalize">{paymentMethod}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Date: {orderDate} | {orderTime}
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Order Summary</h3>
          <hr className="border-gray-200 mb-2" />
          <div className="grid grid-cols-2 gap-y-1">
            <div>Order ID</div>
            <div className="text-right font-medium text-gray-900">{order.orderId || "N/A"}</div>

            <div>Car Number</div>
            <div className="text-right relative cursor-help" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <span className="font-medium text-gray-900">
                {totalCars} car{totalCars !== 1 ? "s" : ""}
              </span>

              {/* Tooltip */}
              {showTooltip && totalCars > 0 && (
                <div
                  className="absolute z-50 px-3 py-2 text-xs bg-gray-900 text-white rounded-md shadow-lg max-w-xs break-words"
                  style={{
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginTop: "8px",
                  }}
                >
                  <div className="font-semibold mb-1">Car Numbers:</div>
                  <div>{carNumbersText}</div>
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              )}
            </div>

            <div>Order Amount</div>
            <div className="text-right">{totalOrderAmount} QAR</div>

            <div>Previous Received Amount</div>
            <div className="text-right font-semibold text-gray-900">{totalReceivedAmount} QAR</div>

            <div>Remaining Amount</div>
            <div className="text-right font-semibold text-gray-900">{remainingAmount} QAR</div>
          </div>
        </div>

        {/* Payment Status */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Payment Status</h3>
          <hr className="border-gray-200 mb-2" />
          <div className="grid grid-cols-2 gap-y-2">
            <div>Received Amount</div>
            <div className="text-right">
              <input
                type="number"
                value={receivedPrice || ""}
                onChange={handleReceivedPriceChange}
                disabled={isProcessing}
                className={`border rounded-md px-3 py-1.5 w-32 text-right focus:outline-none ${
                  errorText ? "border-red-500 focus:ring-2 focus:ring-red-400" : "border-gray-300 focus:ring-2 focus:ring-blue-500"
                } ${isProcessing ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
              {errorText && <div className="text-xs text-red-500 text-right mt-1">{errorText}</div>}
            </div>

            <div>Remaining Amount</div>
            <div className="text-right font-medium text-gray-900">{remainingAmount} QAR</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 border-t pt-4 mt-2">
          <button 
            onClick={handleCloseModal} 
            disabled={isProcessing}
            className="px-5 py-2 rounded-full border border-blue-500 text-blue-500 hover:bg-blue-50 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleCompletePayment}
            disabled={Number(receivedPrice) !== remainingAmount || receivedPrice <= 0 || isProcessing}
            className={`px-5 py-2 rounded-full font-medium transition ${
              Number(receivedPrice) === remainingAmount && !isProcessing
                ? "bg-blue-500 text-white hover:bg-blue-600" 
                : "bg-blue-300 text-white cursor-not-allowed"
            }`}
          >
            {isProcessing ? "Processing..." : "Complete"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CashModal;